import { useEffect, useState } from 'react'
import { Leaf } from 'lucide-react'
import { Button } from '../ui/Button'
import { HabitAdjustmentDialog } from './HabitAdjustmentDialog'
import type {
  HabitAdjustmentCheckin,
  HabitAdjustmentChooseActionResponse,
  HabitAdjustmentConfirmResponse,
  HabitAdjustmentDecisionResponse,
  HabitAdjustmentRespondResponse,
  HabitAdjustmentSimpleResponse,
} from '../../services/habitAdjustmentService'

type DialogStep = 'diagnose' | 'pause'

function getWhyText(checkin: HabitAdjustmentCheckin): string | null {
  // Defect 2026-08-29: this used to return 'No original reason captured yet.'
  // — an internal note about OUR missing data, shown to the user under a
  // heading. With nothing captured, the whole field (label included) is
  // omitted rather than filled with a placeholder.
  const description = checkin.habit_description?.trim()
  return description ? description : null
}

function getMissPattern(checkin: HabitAdjustmentCheckin) {
  // Framed as recent rhythm, not a scorecard. The card is an offer of
  // help, so this block must never read as a guilt tally (P2C doctrine).
  const missed = typeof checkin.miss_count === 'number' ? checkin.miss_count : null
  const expected = typeof checkin.expected_count === 'number' ? checkin.expected_count : null

  if (missed !== null && expected !== null && expected > 0) {
    const landed = Math.max(expected - missed, 0)
    return `${landed} of ${expected} lately`
  }
  if (missed !== null && expected !== null) return `${Math.max(expected - missed, 0)} lately`
  return 'A rough stretch'
}

function getDetectedWindow(checkin: HabitAdjustmentCheckin) {
  const start = checkin.detected_window_start?.slice(0, 10)
  const end = checkin.detected_window_end?.slice(0, 10)
  if (start && end) return `${start} to ${end}`
  if (end) return `Through ${end}`
  return 'Recent stretch'
}

export function HabitAdjustmentCheckinCard({
  checkin,
  markShown,
  respond,
  diagnose,
  chooseAction,
  confirm,
  pause,
  onDone,
}: {
  checkin: HabitAdjustmentCheckin
  markShown: (checkinId: string) => void
  respond: (
    checkinId: string,
    response: 'adjust' | 'pause' | 'keep_as_is' | 'not_now',
  ) => Promise<HabitAdjustmentRespondResponse>
  diagnose: (checkinId: string, reasons: string[]) => Promise<HabitAdjustmentDecisionResponse>
  chooseAction: (
    decisionId: string,
    actionId: string,
    details?: Record<string, unknown>,
  ) => Promise<HabitAdjustmentChooseActionResponse>
  confirm: (decisionId: string) => Promise<HabitAdjustmentConfirmResponse>
  pause: (
    habitDefId: number,
    payload: { paused_until?: string; reason_codes?: string[]; source?: string },
  ) => Promise<HabitAdjustmentSimpleResponse>
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [initialStep, setInitialStep] = useState<DialogStep>('diagnose')
  const [working, setWorking] = useState(false)

  useEffect(() => {
    markShown(checkin.id)
  }, [checkin.id, markShown])

  const openAdjust = async () => {
    setInitialStep('diagnose')
    setWorking(true)
    try {
      // Mark the check-in 'opened' so telemetry/state reflect intent before
      // the user starts diagnosing. Mutates check-in only, never the habit.
      await respond(checkin.id, 'adjust').catch(() => undefined)
    } finally {
      setWorking(false)
      setOpen(true)
    }
  }

  const openPause = async () => {
    setInitialStep('pause')
    setWorking(true)
    try {
      await respond(checkin.id, 'pause').catch(() => undefined)
    } finally {
      setWorking(false)
      setOpen(true)
    }
  }

  const handleKeep = async () => {
    setWorking(true)
    try {
      await respond(checkin.id, 'keep_as_is').catch(() => undefined)
      onDone()
    } finally {
      setWorking(false)
    }
  }

  const handleNotNow = async () => {
    setWorking(true)
    try {
      await respond(checkin.id, 'not_now').catch(() => undefined)
      onDone()
    } finally {
      setWorking(false)
    }
  }

  return (
    <>
      <div className="p-3 rounded-xl border border-soft bg-surface">
        <div className="flex items-center gap-2">
          <Leaf className="w-4 h-4 text-brand" />
          <p className="text-sm font-semibold text-primary">
            {checkin.title || `Gentle adjustment for ${checkin.habit_title}`}
          </p>
        </div>
        <p className="mt-1 text-xs text-secondary">
          {checkin.body || `You’ve had a rough stretch with ${checkin.habit_title}. We can make this easier without throwing it away.`}
        </p>
        <div
          className={`mt-3 grid grid-cols-1 gap-2 ${
            getWhyText(checkin) ? 'sm:grid-cols-[1fr_auto]' : 'sm:grid-cols-1 sm:justify-items-start'
          }`}
        >
          {getWhyText(checkin) && (
            <div className="rounded-lg border border-soft bg-surface-alt px-2 py-2">
              <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Why it matters</p>
              <p className="mt-1 text-xs text-secondary leading-relaxed">{getWhyText(checkin)}</p>
            </div>
          )}
          <div className="rounded-lg border border-soft bg-surface-alt px-2 py-2 sm:min-w-36">
            <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Recently</p>
            <p className="mt-1 text-xs font-semibold text-primary">{getMissPattern(checkin)}</p>
            <p className="mt-0.5 text-app-micro text-muted">{getDetectedWindow(checkin)}</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button
            size="sm"
            disabled={working}
            onClick={() => void openAdjust()}
            className="bg-brand hover:bg-brand-hover text-on-brand"
          >
            Adjust this
          </Button>
          <Button size="sm" variant="outline" disabled={working} onClick={() => void openPause()}>
            Pause for now
          </Button>
          <Button size="sm" variant="outline" disabled={working} onClick={() => void handleKeep()}>
            Keep as is
          </Button>
          <Button size="sm" variant="ghost" disabled={working} onClick={() => void handleNotNow()}>
            Not now
          </Button>
        </div>
      </div>
      {open && (
        <HabitAdjustmentDialog
          open={open}
          onOpenChange={setOpen}
          checkin={checkin}
          initialStep={initialStep}
          respond={respond}
          diagnose={diagnose}
          chooseAction={chooseAction}
          confirm={confirm}
          onPause={pause}
          onDone={onDone}
        />
      )}
    </>
  )
}
